<script setup lang="ts">
import { storeToRefs } from "pinia";
import { computed, onBeforeUnmount, ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import MobileMenu from "./MobileMenu.vue";
import ThemeModeToggle from "./ThemeModeToggle.vue";

import { Sparkles, ChevronsUpDown, X } from "@lucide/vue";

import OrganizationsDropdown from "./OrganizationsDropdown.vue";
import NavSlashDivider from "./NavSlashDivider.vue";
import FeedbackWidget from "@features/Feedback/FeedbackWidget.vue";
import { organizationsStore } from "@/stores/Utils/auth/Organizations";
import { mobileMenuStore } from "@/stores/Utils/Mobile/MobileMenu";

import { useChatPanelStore } from "@stores/chat/chatPanel.store";
import { useTenantFeatureAccess } from "@/composables/useTenantFeatureAccess";

import { useProjectWrapper } from "@/dataProviders/ProjectWrapper";
import { useRecentProjects } from "@/composables/useRecentProjects";
import { useProjectSwitcherProjects } from "@/composables/useProjectSwitcherProjects";
import { useProjectRevisionsStore } from "@/pages/ProjectVersionHistory/stores/projectRevisions.store";

import Popover from "@/components/Reusables/Popover/Popover.vue";
import ProjectSwitcherDropdown from "./ProjectSwitcherDropdown.vue";
// Third step of the switcher trail: which revision of the project you are looking at. It lives here
// rather than in the project toolbar so the trail reads organization / project / revision. It reads
// the shared revisions store, which ProjectDetailLayout fills and keeps current, and renders nothing
// until that store has revisions for the route's project.
import ProjectRevisionSwitcher from "@/pages/ProjectVersionHistory/components/revisions/ProjectRevisionSwitcher.vue";

import Logo from "../../assets/Logos/LoadingLogo.svg";

defineProps<{
  lCAdaIsShown: boolean;
}>();

const { canUseAiChat } = useTenantFeatureAccess();

const chatPanelStore = useChatPanelStore();
const { isOpen: isChatOpen } = storeToRefs(chatPanelStore);

const useMobileMenuStore = mobileMenuStore();

const projectWrapper = useProjectWrapper();

// The project is displayed under the previewed revision's saved name while previewing a FROZEN
// revision, and under its live row name otherwise (the current revision's state is the live head).
const revisionsStore = useProjectRevisionsStore();

// Display the project name as soon as we can identify it. We try, in order:
//   1. The ProjectWrapper — authoritative full data, but its fetch is slow.
//   2. The switcher's pre-loaded project list — id+name cached at session start
//      so the name appears instantly on navigation while the heavy wrapper
//      fetch is still in flight.
// Anything that doesn't match the current route's :id is treated as stale and
// hidden, so we never display a previous project's name (in this org or
// another) after switching. A previewed frozen revision's saved name
// takes precedence over both sources.
const projectName = computed(() => {
  const routeProjectId = typeof route.params.id === "string" ? route.params.id : undefined;
  if (!routeProjectId) return "";
  const wrapperProject = projectWrapper.getProjectWrapper().project;
  const liveName =
    wrapperProject?.id === routeProjectId
      ? wrapperProject.projectName ?? ""
      : switcherProjects.value.find((project) => project.id === routeProjectId)?.name ?? "";
  return revisionsStore.displayProjectNameFor(routeProjectId, liveName);
});

const { recents } = useRecentProjects(5);
const {
  projects: switcherProjects,
  isLoading: switcherProjectsLoading,
  refetch: refetchSwitcherProjects,
} = useProjectSwitcherProjects();

const route = useRoute();
const router = useRouter();

const currentProjectId = computed(() => (typeof route.params.id === "string" ? route.params.id : ""));

/**
 * Inside a project, decided by the URL rather than by a list of route names.
 *
 * It WAS a list, and the list drifted: the settings page's route was renamed to `projectSettings`
 * and nothing here was told, so every settings URL fell through to the "All projects" label while
 * sitting inside a project. `projectLcaPdfReportPage` had never been added either. A hardcoded list
 * of every page under a layout is a promise to remember this file whenever a page is added, and it
 * was already broken twice.
 *
 * Every project page is a child of `/project/:id` — the ProjectDetailLayout branch — except the edit
 * wizard, which is the top-level `/project/:id/edit`. Both start with the same prefix in
 * `route.matched`, so one `startsWith` covers the layout's children, the wizard, and anything added
 * to either later. The id check keeps `/create-project` out; it has no `:id`.
 */
const isOnProjectRoute = computed(
  () => currentProjectId.value.length > 0 && route.matched.some((record) => record.path.startsWith("/project/:id"))
);

// The switcher trigger always shows: the current project's name when inside a project, otherwise
// "All projects" as the fallback label (the dropdown then lists every project / recents).
const projectSwitcherLabel = computed(() => (isOnProjectRoute.value && projectName.value ? projectName.value : "All projects"));

// Switching the current revision restores that revision's name onto the project row, which makes
// this cached id+name list — the one the dropdown lists and the label falls back to — stale. The
// label recovers on its own once ProjectDetailLayout re-reads the ProjectWrapper; the list needs
// this. Same key shape as that layout's watcher: the project id rides along so navigating to
// another project and the pointer subscription's first emission are not mistaken for a switch.
watch(
  () => `${currentProjectId.value}|${revisionsStore.activeMetaFor(currentProjectId.value)?.id ?? ""}`,
  (next, previous) => {
    const [nextProjectId, nextRevisionId] = next.split("|");
    const [previousProjectId, previousRevisionId] = previous.split("|");
    if (nextProjectId !== previousProjectId || !nextRevisionId || !previousRevisionId) return;

    void refetchSwitcherProjects();
  }
);

// Deselect the current project — navigate back to the projects list, which then shows "All projects".
const clearProject = () => {
  router.push("/");
};

const emits = defineEmits(["toggleLCAda"]);

const handleToggleLCAda = (shown: boolean) => {
  emits("toggleLCAda", shown);
};

const ORG_TRIGGER_ID = "topnav-org-trigger";
const showOrgDropdown = ref(false);
const orgDropdownContainerRef = ref<HTMLElement | null>(null);

const closeOrgDropdown = () => {
  showOrgDropdown.value = false;
};
const toggleOrgDropdown = () => {
  const willOpen = !showOrgDropdown.value;
  if (willOpen && !useMobileMenuStore.getIsHidden()) {
    useMobileMenuStore.toggleIsHidden();
  }
  showOrgDropdown.value = willOpen;
};

const handleDocumentClick = (event: MouseEvent) => {
  if (!orgDropdownContainerRef.value) return;
  if (!orgDropdownContainerRef.value.contains(event.target as Node)) {
    closeOrgDropdown();
  }
};
const handleEscape = (event: KeyboardEvent) => {
  if (event.key === "Escape") closeOrgDropdown();
};

watch(showOrgDropdown, (open) => {
  if (open) {
    document.addEventListener("click", handleDocumentClick);
    document.addEventListener("keydown", handleEscape);
  } else {
    document.removeEventListener("click", handleDocumentClick);
    document.removeEventListener("keydown", handleEscape);
  }
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleDocumentClick);
  document.removeEventListener("keydown", handleEscape);
});
</script>

<template>
  <div class="z-sticky relative border-b border-slate-200 bg-slate-50">
    <div class="flex h-14 w-full">
      <!-- Logo column is fixed at 56px so its right divider lines up with the collapsed main
           sidebar's edge (56px) in the column directly below it. -->
      <RouterLink
        to="/"
        aria-label="Home"
        class="focus-ring flex h-full shrink-0 items-center pl-2 md:w-[56px] md:border-r md:border-slate-200">
        <img
          :src="Logo"
          class="h-[30px] w-[45px] shrink-0 p-0.5"
          alt="" />
      </RouterLink>
      <!-- Everything to the right of the logo column: switchers (left) + controls (right). -->
      <div class="flex min-w-0 flex-1 items-center justify-between">
        <!-- Left navigation. pl-px-18 lines the org name up with the sub-nav content (also at 18px)
             when the sub-nav is open beside the collapsed main sidebar. -->
        <div class="pl-px-18 relative flex items-center py-2 pr-2.5">
          <div
            ref="orgDropdownContainerRef"
            class="relative mt-1.5 pr-1">
            <!-- The flow-guide "Organization" step targets this wrapper (org name + chevron) so the
               spotlight encloses the whole switcher, not just the small chevron toggle. -->
            <div
              :id="ORG_TRIGGER_ID"
              class="text-type-default gap-px-4 flex items-center py-1 text-sm font-medium">
              <button
                type="button"
                :aria-expanded="showOrgDropdown"
                aria-haspopup="menu"
                class="focus-ring -ml-1 rounded-md px-1 py-0.5 leading-none"
                @click="toggleOrgDropdown">
                <span>{{ organizationsStore().getCurrentTenant()?.name }}</span>
              </button>
              <button
                type="button"
                :aria-expanded="showOrgDropdown"
                aria-haspopup="menu"
                aria-label="Change organization"
                class="focus-ring hover:bg-app-blue-200/30 duration-fast ease-standard inline-flex items-center justify-center rounded-md p-1 leading-none transition"
                @click="toggleOrgDropdown">
                <ChevronsUpDown
                  :stroke-width="1.8"
                  class="icon-sm text-app-blue-800/60 shrink-0"
                  aria-hidden="true" />
              </button>
            </div>

            <Transition name="fade-top">
              <OrganizationsDropdown
                v-if="showOrgDropdown"
                @close="closeOrgDropdown" />
            </Transition>
          </div>
          <Popover
            class="mt-1.5"
            placement="bottom-start"
            role="menu"
            aria-label="Switch project">
            <template #trigger="{ toggle, isOpen }">
              <div class="group gap-px-4 flex items-center px-2 py-1 pl-5.5">
                <NavSlashDivider />
                <button
                  type="button"
                  :aria-expanded="isOpen"
                  aria-haspopup="menu"
                  class="focus-ring rounded-md px-1 py-0.5 leading-none"
                  @click="toggle">
                  <span class="text-app-blue-900 text-sm font-medium">{{ projectSwitcherLabel }}</span>
                </button>
                <!-- id targeted by the "Switch Project" onboarding flow guide step (RTLCA-5639) -->
                <button
                  id="switch-project-nav-button"
                  type="button"
                  :aria-expanded="isOpen"
                  aria-haspopup="menu"
                  aria-label="Switch project"
                  class="focus-ring hover:bg-app-blue-200/30 duration-fast ease-standard inline-flex items-center justify-center rounded-md p-1 leading-none transition"
                  @click="toggle">
                  <ChevronsUpDown
                    :stroke-width="1.8"
                    class="icon-sm text-app-blue-800/60 shrink-0"
                    aria-hidden="true" />
                </button>
                <button
                  v-if="isOnProjectRoute && projectName"
                  type="button"
                  :aria-label="`Clear ${projectSwitcherLabel}`"
                  class="focus-ring hover:bg-app-blue-200/30 duration-fast ease-standard inline-flex items-center justify-center rounded-md p-1 leading-none opacity-0 transition group-hover:opacity-100 focus-visible:opacity-100"
                  @click.stop="clearProject">
                  <X
                    :stroke-width="1.8"
                    class="icon-sm text-app-blue-800/60 shrink-0"
                    aria-hidden="true" />
                </button>
              </div>
            </template>
            <template #default="{ close }">
              <ProjectSwitcherDropdown
                :recents="recents"
                :all-projects="switcherProjects"
                :is-loading="switcherProjectsLoading"
                @navigate="close" />
            </template>
          </Popover>
          <ProjectRevisionSwitcher
            v-if="isOnProjectRoute"
            class="mt-1.5 min-w-0"
            :project-id="currentProjectId" />
        </div>
        <!-- Right navigation -->
        <div class="hidden items-center gap-2 p-2 px-2.5 md:flex">
          <ThemeModeToggle />
          <FeedbackWidget />
          <button
            v-if="canUseAiChat"
            id="assistant-nav-button"
            v-tooltip="{ text: 'AI Agent', variant: 'label', placement: 'bottom' }"
            type="button"
            :class="
              isChatOpen
                ? 'ring-app-blue-500 bg-app-blue-200/20 hover:bg-app-blue-200/40'
                : 'bg-slate-0 text-type-default ring-slate-250 hover:bg-slate-100 hover:ring-slate-300'
            "
            class="p-px-8 focus-ring interactive-active duration-fast ease-standard rounded-full ring-1 transition-all"
            :aria-label="isChatOpen ? 'Close AI Agent' : 'Open AI Agent'"
            :aria-pressed="isChatOpen"
            @click="chatPanelStore.toggle()">
            <Sparkles
              fill="var(--color-app-blue-100)"
              color="var(--color-app-blue-800)"
              class="icon-sm"
              :stroke-width="1.5" />
          </button>
        </div>
        <div class="flex items-center justify-center p-2 px-2.5 md:hidden">
          <MobileMenu
            :l-c-ada-is-shown="lCAdaIsShown"
            @toggle-l-c-ada="handleToggleLCAda" />
        </div>
      </div>
    </div>
  </div>
</template>
